import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { doorService } from '../services';

/**
 * The persistent event-day strip, both sides of the same door:
 *
 *   host / co-host → LIVE · <event> · tap to scan tickets  →  /host/events/:id/door
 *   ticket holder  → YOUR TICKET · <event> · tap to show it →  /ticket/:code
 *
 * Both use the same window (midnight on the event's day, or two hours before —
 * whichever comes first, through 12h after the end), because the person at the
 * door and the person in the queue should agree about when the night has started.
 *
 * Hosting wins when you're both: you're working, not attending. Hidden when not
 * authenticated, when there's nothing on, and on the page the strip points at.
 *
 * A checked-in ticket keeps its strip. Retiring it on scan seemed tidy and was
 * wrong: people step out and come back and get re-scanned, and you may want the
 * ticket itself afterwards. It just stops telling you to show it at the door.
 *
 * Swipe it left and it tucks into a tab on the left edge at the same height;
 * swipe or tap the tab to bring it back. Collapsed state is remembered per event,
 * so it stays out of the way while you use the app but a different night starts
 * fresh.
 *
 * The tab carries an ×. It puts up a short prompt in the tab's place — HIDE IT
 * FOR 30 MIN? SWIPE RIGHT → — for HINT_MS. Swiping it right (or clicking it, with a mouse or keyboard) hides the
 * strip and its tab outright for DISMISS_MINUTES; left alone it fades and leaves
 * the tab. It's never gone for good — it's the way back to the door, so the
 * cool-off lapses on its own. (A long left swipe on the strip still jumps
 * straight to the cool-off.)
 *
 * Two things at once — a night with two ticketed rooms, or a host running two
 * doors — get a ⌃ on the right that opens a sheet of everything you're on
 * tonight. Doors and tickets are listed separately, because you might be working
 * one and attending the other.
 */
const COLLAPSED_KEY = 'pr.liveBanner.collapsed';
/* Fully dismissed until this timestamp. Separate from COLLAPSED_KEY on purpose:
   collapsing tucks the strip to a tab you can pull back, dismissing makes it
   gone. A dismissal that lasted forever would hide the door you're working, so
   it lapses on its own. */
const DISMISSED_KEY = 'pr.liveBanner.dismissedUntil';
const DISMISS_MINUTES = 30;
/** How long the "hide it for longer?" prompt stays up after a close. */
export const HINT_MS = 6000;
/** How far the prompt has to travel right to count. */
export const HINT_SWIPE_PX = 90;

function readCollapsed() {
  try { return localStorage.getItem(COLLAPSED_KEY) || ''; } catch { return ''; }
}
function readDismissedUntil() {
  try { return Number(localStorage.getItem(DISMISSED_KEY)) || 0; } catch { return 0; }
}
export default function LiveEventBanner() {
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [doors, setDoors] = useState([]);
  const [tickets, setTickets] = useState([]);
  // The event id this is collapsed for, '' when open.
  const [collapsedFor, setCollapsedFor] = useState(readCollapsed);
  // The strip floats 80px up on phones (above the 64px tab bar) and 16px on wider screens —
  // `.pr-live-banner-bottom` in index.css. Event pages add a sticky price + CTA bar above the
  // tab bar and publish its height as --pr-sticky-cta (surface/StickyCtaBar); sitting on top
  // of that bar would swallow the GET TICKETS tap, so the class moves the strip up by that much.
  const [drag, setDrag] = useState(0);
  const [dismissedUntil, setDismissedUntil] = useState(readDismissedUntil);
  const [sheetOpen, setSheetOpen] = useState(false);
  const dragStart = useRef(null);
  // The "hide it for longer?" prompt after a close, and how far it has been dragged.
  const [hint, setHint] = useState(false);
  const [hintDrag, setHintDrag] = useState(0);
  const hintStart = useRef(null);
  const hintTimer = useRef(null);
  // The pointer behind the last press on the prompt: a touch has to swipe, a mouse or keyboard clicks.
  const hintPointer = useRef(null);

  const refresh = useCallback(async () => {
    if (!isAuthenticated) { setDoors([]); setTickets([]); return; }
    const [d, t] = await Promise.all([doorService.active(), doorService.activeTickets()]);
    setDoors(d.success && Array.isArray(d.data) ? d.data : []);
    setTickets(t.success && Array.isArray(t.data) ? t.data : []);
  }, [isAuthenticated]);

  useEffect(() => {
    refresh();
    if (!isAuthenticated) return undefined;
    // Re-poll every 3 minutes so the strip appears/disappears on its own.
    const id = setInterval(refresh, 180000);
    return () => clearInterval(id);
  }, [refresh, isAuthenticated]);

  useEffect(() => {
    const left = dismissedUntil - Date.now();
    if (left <= 0) return undefined;
    const id = setTimeout(() => setDismissedUntil(0), left);
    return () => clearTimeout(id);
  }, [dismissedUntil]);

  const collapse = (eventId) => {
    setCollapsedFor(eventId);
    try { localStorage.setItem(COLLAPSED_KEY, eventId); } catch { /* storage off */ }
  };
  const hideHint = useCallback(() => {
    clearTimeout(hintTimer.current);
    setHint(false);
    setHintDrag(0);
    hintStart.current = null;
  }, []);
  /** The tab's ×: offer the longer hide. */
  const close = () => {
    clearTimeout(hintTimer.current);
    setHint(true);
    hintTimer.current = setTimeout(hideHint, HINT_MS);
  };
  useEffect(() => () => clearTimeout(hintTimer.current), []);
  const expand = () => {
    setCollapsedFor('');
    try { localStorage.removeItem(COLLAPSED_KEY); } catch { /* storage off */ }
  };
  const dismiss = () => {
    const until = Date.now() + DISMISS_MINUTES * 60000;
    setDismissedUntil(until);
    try { localStorage.setItem(DISMISSED_KEY, String(until)); } catch { /* storage off */ }
    hideHint();
  };

  if (!isAuthenticated) return null;
  if (Date.now() < dismissedUntil) return null;

  // Everything on tonight, whichever side of the door you're on. Doors first:
  // if you're working one, that's the job, and attending is the side quest.
  const doorItems = doors.map((d) => ({
    key: `door-${d.eventId}`,
    kind: 'door',
    eventId: d.eventId,
    title: d.title,
    startsAt: d.startsAt,
    badge: 'DOOR',
    to: `/host/events/${d.eventId}/door`,
  }));
  // No ticket row yet (free RSVP, or one that hasn't been issued) — /my-tickets
  // still gets them somewhere useful rather than hiding the strip entirely.
  const ticketItems = tickets.map((t) => ({
    key: `ticket-${t.eventId}`,
    kind: 'ticket',
    eventId: t.eventId,
    title: t.title,
    startsAt: t.startsAt,
    checkedIn: t.checkedIn,
    ticketCount: t.ticketCount,
    badge: t.checkedIn ? 'IN' : 'TICKET',
    note: t.ticketCount > 1 ? `${t.ticketCount} tickets` : null,
    to: t.ticketCode ? `/ticket/${t.ticketCode}` : '/my-tickets',
  }));
  const items = [...doorItems, ...ticketItems];
  const totalItems = items.length;
  if (totalItems === 0) return null;

  // Never point at the page you're already on — but with two events on, that's a
  // reason to show the *other* one, not to disappear.
  const primary = items.find((i) => i.to !== location.pathname);
  if (!primary) return null;

  const hosting = primary.kind === 'door';
  const target = primary.to;

  const collapsed = collapsedFor === primary.eventId;
  const label = hosting ? 'DOOR' : (primary.checkedIn ? 'IN' : 'TICKET');
  const accent = hosting ? '#03FFFF' : (primary.checkedIn ? '#9CA3AF' : '#03FF00');

  // Horizontal drag: left past the threshold tucks it away, right brings it back.
  const onTouchStart = (e) => { dragStart.current = e.touches[0].clientX; };
  const onTouchMove = (e) => {
    if (dragStart.current == null) return;
    const dx = e.touches[0].clientX - dragStart.current;
    // Only the direction that does something: left when open, right when tucked.
    setDrag(collapsed ? dx : Math.min(0, dx));
  };
  const onTouchEnd = () => {
    const dx = drag;
    dragStart.current = null;
    setDrag(0);
    // Two left-swipe depths: a short one tucks it to the edge tab, a long one
    // sends it away for DISMISS_MINUTES. Keeping the tuck means the shorter,
    // more common gesture still does the thing it always did.
    if (!collapsed && dx < -140) { dismiss(); return; }
    if (!collapsed && dx < -60) collapse(primary.eventId);
    // The tucked tab can be swiped away too — otherwise the only way to be rid
    // of it is to pull it back out first.
    if (collapsed && dx < -40) { dismiss(); return; }
    if (collapsed && dx > 40) expand();
  };

  // The prompt stands in for the tab while it's up (they'd share the same spot).
  // Touch: drag it right past HINT_SWIPE_PX. Mouse / keyboard: a click is the same.
  if (collapsed && hint) {
    const onHintDown = (e) => {
      hintPointer.current = e.pointerType;
      if (e.pointerType === 'mouse') return;
      hintStart.current = e.clientX;
      clearTimeout(hintTimer.current); // holding it keeps it up
      e.currentTarget.setPointerCapture?.(e.pointerId);
    };
    const onHintMove = (e) => {
      if (hintStart.current == null) return;
      setHintDrag(Math.max(0, e.clientX - hintStart.current));
    };
    const onHintUp = () => {
      if (hintStart.current == null) return;
      const dx = hintDrag;
      hintStart.current = null;
      setHintDrag(0);
      if (dx >= HINT_SWIPE_PX) { dismiss(); return; }
      hintTimer.current = setTimeout(hideHint, HINT_MS / 2);
    };
    const progress = Math.min(1, hintDrag / HINT_SWIPE_PX);
    return (
      <div className="pr-live-banner-bottom fixed inset-x-0 z-40 flex justify-center px-3" data-testid="live-banner-hint">
        <button
          type="button"
          onClick={() => {
            const touch = hintPointer.current === 'touch' || hintPointer.current === 'pen';
            hintPointer.current = null;
            if (!touch) dismiss();
          }}
          onPointerDown={onHintDown}
          onPointerMove={onHintMove}
          onPointerUp={onHintUp}
          onPointerCancel={onHintUp}
          aria-label={`Hide the ${label.toLowerCase()} bar for ${DISMISS_MINUTES} minutes`}
          className="relative flex w-full max-w-md touch-pan-y select-none items-center gap-3 overflow-hidden border-2 border-black bg-black px-3 py-2.5 text-left shadow-lg"
          style={{ animation: 'prLiveHintIn 180ms ease-out' }}
        >
          <style>{'@keyframes prLiveHintIn { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: none; } }'}</style>
          {/* The trail fills in behind the thumb as it travels. */}
          <span aria-hidden className="absolute inset-y-0 left-0 bg-white/15" style={{ width: `${progress * 100}%` }} />
          <span
            aria-hidden
            className="relative flex h-8 w-8 shrink-0 items-center justify-center bg-white font-mono text-[14px] font-bold text-black"
            style={{ transform: `translateX(${hintDrag}px)`, transition: hintDrag ? 'none' : 'transform 160ms' }}
          >
            →
          </span>
          <span className="relative min-w-0 flex-1" style={{ opacity: 1 - progress * 0.6 }}>
            <span className="block truncate font-mono text-xs font-bold uppercase tracking-wider text-white">
              Hide it for {DISMISS_MINUTES} min?
            </span>
            <span className="block font-mono text-[10px] uppercase tracking-wider text-gray-300">
              Swipe right →
            </span>
          </span>
        </button>
      </div>
    );
  }

  if (collapsed) {
    return (
      <div
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
        className="pr-live-banner-bottom fixed left-0 z-40 flex flex-col items-stretch border-2 border-l-0 border-black bg-black shadow-lg"
        style={{ transform: `translateX(${Math.min(drag, 40)}px)`, transition: drag ? 'none' : 'transform 160ms' }}
        data-testid="live-banner-tab"
      >
        {/* × — offers to hide it (tab and all) for a while. */}
        <button
          type="button"
          onClick={close}
          aria-label={`Close ${label.toLowerCase()} bar`}
          data-testid="live-banner-close"
          className="flex h-8 items-center justify-center border-b border-white/20 text-white/70 hover:text-white"
        >
          <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="square" aria-hidden="true">
            <path d="M6 6l12 12M18 6L6 18" />
          </svg>
        </button>
        <button
          type="button"
          onClick={expand}
          aria-label={`Show ${label.toLowerCase()} bar`}
          className="flex items-center gap-1.5 py-3 pl-2 pr-2.5"
        >
          <span className="inline-block h-2 w-2 animate-pulse rounded-full" style={{ background: accent }} />
          <span
            className="font-mono text-[9px] font-bold uppercase tracking-[0.16em] text-white"
            style={{ writingMode: 'vertical-rl' }}
          >
            {label}
          </span>
        </button>
      </div>
    );
  }

  return (
    <div className="pr-live-banner-bottom fixed inset-x-0 z-40 flex justify-center px-3" data-testid="live-event-banner">
      <button
        type="button"
        onClick={() => navigate(target)}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
        className="flex w-full max-w-md items-center justify-between gap-3 border-2 border-black bg-black px-4 py-2.5 text-left shadow-lg"
        style={{
          transform: `translateX(${drag}px)`,
          opacity: drag ? Math.max(0.35, 1 + drag / 220) : 1,
          transition: drag ? 'none' : 'transform 160ms, opacity 160ms',
        }}
      >
        <span className="flex min-w-0 items-center gap-2">
          <span
            className="inline-block h-2.5 w-2.5 shrink-0 animate-pulse rounded-full"
            style={{ background: hosting ? '#03FFFF' : (primary.checkedIn ? '#9CA3AF' : '#03FF00') }}
          />
          <span className="min-w-0">
            <span className="block truncate font-mono text-xs font-bold uppercase tracking-wider text-white">
              {hosting ? 'LIVE' : primary.checkedIn ? "YOU'RE IN" : 'YOUR TICKET'} · {primary.title}
            </span>
            <span className="block font-mono text-[10px] uppercase tracking-wider text-gray-300">
              {hosting ? 'Tap to scan tickets'
                : primary.checkedIn ? 'Checked in · tap to see your ticket'
                  : 'Tap to show it at the door'}
              {!hosting && primary.ticketCount > 1 ? ` · ${primary.ticketCount} tickets` : ''}
            </span>
          </span>
        </span>
        <span className="flex shrink-0 items-center gap-2">
          <span className="border border-white px-2.5 py-1 font-mono text-[10px] font-bold uppercase tracking-widest text-white">
            {label}
          </span>

          {/* Two things on at once — pick which one. */}
          {totalItems > 1 && (
            <span
              role="button"
              tabIndex={0}
              aria-label={`Switch between ${totalItems} events tonight`}
              onClick={(e) => { e.stopPropagation(); setSheetOpen(true); }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') { e.stopPropagation(); setSheetOpen(true); }
              }}
              className="flex items-center gap-1 border border-white px-2 py-1 font-mono text-[10px] font-bold uppercase tracking-widest text-white hover:bg-white hover:text-black"
            >
              <span>{totalItems}</span>
              <svg width="9" height="9" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="square" strokeLinejoin="miter">
                <path d="M2 8l4-4 4 4" />
              </svg>
            </span>
          )}
          {/* Swipe is the mobile gesture; this is the same thing for a mouse. */}
          <span
            role="button"
            tabIndex={0}
            aria-label="Tuck away"
            onClick={(e) => { e.stopPropagation(); collapse(primary.eventId); }}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.stopPropagation(); collapse(primary.eventId); } }}
            className="hidden px-1 font-mono text-[14px] leading-none text-white/60 hover:text-white md:block"
          >
            ‹
          </span>
        </span>
      </button>

      {sheetOpen && createPortal(
        <div
          className="pr-overlay-root fixed inset-0 safe-inset z-[95] flex items-end"
          style={{ background: 'rgba(0,0,0,.45)' }}
          onClick={() => setSheetOpen(false)}
          role="dialog"
          aria-modal="true"
        >
          <div
            className="w-full border-t-2 border-black bg-white"
            onClick={(e) => e.stopPropagation()}
            style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
          >
            <div className="flex justify-center py-2">
              <span className="block h-[5px] w-16 bg-black" aria-hidden />
            </div>

            <div className="flex items-center justify-between border-b-2 border-black px-4 pb-3">
              <span className="font-mono text-[12px] font-bold uppercase tracking-[0.14em]">Tonight</span>
              <button
                type="button"
                onClick={() => setSheetOpen(false)}
                className="font-mono text-[11px] uppercase tracking-[0.1em] text-gray-500 hover:text-black"
              >
                Close
              </button>
            </div>

            <div className="max-h-[60vh] overflow-y-auto">
              {[
                { heading: 'Doors you\u2019re running', items: doorItems },
                { heading: 'Your tickets', items: ticketItems },
              ].filter((g) => g.items.length > 0).map((group) => (
                <div key={group.heading}>
                  <div className="border-b border-gray-200 bg-gray-50 px-4 py-2 font-mono text-[10px] font-bold uppercase tracking-[0.14em] text-gray-500">
                    {group.heading}
                  </div>
                  {group.items.map((item) => (
                    <button
                      key={item.key}
                      type="button"
                      onClick={() => { setSheetOpen(false); navigate(item.to); }}
                      disabled={item.to === location.pathname}
                      className={`flex w-full items-center gap-3 border-b border-gray-200 px-4 py-3 text-left ${
                        item.to === location.pathname ? 'bg-gray-50 opacity-60' : 'hover:bg-gray-100'
                      }`}
                    >
                      <span className="min-w-0 flex-1">
                        <span className="block truncate font-mono text-[13px] font-bold">{item.title}</span>
                        <span className="block font-mono text-[10px] uppercase tracking-[0.08em] text-gray-500">
                          {new Date(item.startsAt).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}
                          {item.note ? ` \u00b7 ${item.note}` : ''}
                        </span>
                      </span>
                      <span className="flex-shrink-0 border-2 border-black px-2 py-1 font-mono text-[9px] font-bold uppercase tracking-[0.12em]">
                        {item.to === location.pathname ? 'HERE' : item.badge}
                      </span>
                    </button>
                  ))}
                </div>
              ))}
            </div>
          </div>
        </div>,
        document.body,
      )}
    </div>
  );
}
